import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import Cookie from 'js-cookie';
import { getParsedParams } from './consts/params';

const maxCycles = 3;
const intevalLenghtMills = 200;

/**
 * Returns true when the GA library is fully initialised.
 * Avoids 'function is always truthy' false positive.
 */
const isGAReady = (ga: UniversalAnalytics.ga | undefined): ga is UniversalAnalytics.ga =>
  typeof ga?.getAll === 'function';

const runGACallback = (onSuccess: (ga: UniversalAnalytics.ga) => void, onFail?: () => void): void => {
  let interval: ReturnType<typeof setInterval>;

  let cycleNumber = 0;

  const { ga } = window;

  if (isGAReady(ga)) {
    onSuccess(ga);
  } else {
    const command = () => {
      const currentGa = window.ga;

      if (isGAReady(currentGa)) {
        clearInterval(interval);
        onSuccess(currentGa);
      } else {
        // timeout on third attempt
        cycleNumber += 1;
        if (cycleNumber >= maxCycles) {
          clearInterval(interval);

          if (onFail) {
            onFail();
          }
        }
      }
    };

    interval = setInterval(command, intevalLenghtMills);
  }
};

/**
 * Returns a promise that resolves into the Google Analytics object
 * @returns {Promise}
 */

const runGACommand = (): Promise<UniversalAnalytics.ga> =>
  new Promise((resolve, reject) => {
    runGACallback((ga) => {
      resolve(ga);
    }, reject);
  });

/**
 * React hook, that calls URL update function for Google Analytics.
 * https://developers.google.com/analytics/devguides/collection/analyticsjs/single-page-applications
 */
const usePageViews = () => {
  const location = useLocation();

  useEffect(() => {
    runGACallback((ga) => {
      ga('set', 'page', location.pathname);
      ga('send', 'pageview');
    });
  }, [location]);
};

const useClientID = () => {
  const params = getParsedParams();

  useEffect(() => {
    if (params.CID) {
      Cookie.set('cid_alt', params.CID, {
        expires: 730, // in days - 2 years
        domain: '.clearvpn.com',
      });
    }
  }, []);
};

/**
 * React hook, that parses query params from `/profile` route, and sends them to Google Analytics
 */
const useQueryAnalytics = () => {
  const params = getParsedParams();
  const { CID, PRODUCT, BUNDLE, VERSION, OS, LANG } = params;

  useEffect(() => {
    if (CID) {
      window.dataLayer?.push({
        cid: CID,
        product: PRODUCT,
        bundle: BUNDLE,
        version: VERSION,
        os: OS,
        lang: LANG,
      });
      runGACallback((ga) => {
        ga('set', 'cid', CID);
        ga('send', 'pageview');
      });
    }
  }, []);
};

export { usePageViews, useClientID, useQueryAnalytics, runGACommand };
